/**
 * CLI output utilities
 * Provides consistent formatting for CLI output
 * Supports NO_COLOR (https://no-color.org/), --no-color flag, and TTY detection
 */

import ora, { type Ora } from 'ora';
import {
  primary as primaryStyle,
  success as successStyle,
  warning as warningStyle,
  error as errorStyle,
  info as infoStyle,
  muted as mutedStyle,
  bold as boldStyle,
  symbols,
} from '../ui/theme/index.js';

/**
 * Whether the output is a TTY (interactive terminal)
 */
export const isTTY = process.stdout.isTTY === true;

/**
 * Whether color output is disabled via NO_COLOR env or --no-color flag
 */
export const isColorDisabled =
  'NO_COLOR' in process.env ||
  process.argv.includes('--no-color') ||
  process.env.TERM === 'dumb';

/**
 * Whether to use colors (TTY and not explicitly disabled)
 */
export const useColors = isTTY && !isColorDisabled;

/**
 * Identity function for when colors are disabled
 */
const identity = (s: string) => s;

// Color scheme matching Paean brand (respects NO_COLOR and TTY detection)
export const colors = {
  primary: useColors ? primaryStyle : identity,
  success: useColors ? successStyle : identity,
  warning: useColors ? warningStyle : identity,
  error: useColors ? errorStyle : identity,
  info: useColors ? infoStyle : identity,
  dim: useColors ? mutedStyle : identity,
  bold: useColors ? boldStyle : identity,
};

/**
 * Print a success message
 */
export function success(message: string): void {
  console.log(colors.success(symbols.success), message);
}

/**
 * Print an error message
 */
export function error(message: string): void {
  console.error(colors.error(symbols.error), message);
}

/**
 * Print a warning message
 */
export function warning(message: string): void {
  console.log(colors.warning(symbols.warning), message);
}

/**
 * Print an info message
 */
export function info(message: string): void {
  console.log(colors.info(symbols.info), message);
}

/**
 * Print a primary colored message
 */
export function primary(message: string): void {
  console.log(colors.primary(message));
}

/**
 * Print a dim/muted message
 */
export function dim(message: string): void {
  console.log(colors.dim(message));
}

/**
 * Create a spinner (disabled when not a TTY)
 */
export function spinner(text: string): Ora {
  return ora({
    text,
    color: 'cyan',
    isSilent: !isTTY,
  });
}

/**
 * Print a header/title
 */
export function header(text: string): void {
  console.log();
  console.log(colors.bold(colors.primary(text)));
  console.log(colors.dim('─'.repeat(text.length + 4)));
}

/**
 * Print a table row
 */
export function tableRow(label: string, value: string, labelWidth = 20): void {
  const paddedLabel = label.padEnd(labelWidth);
  console.log(`  ${colors.dim(paddedLabel)} ${value}`);
}

/**
 * Print a list item
 */
export function listItem(text: string, indent = 0): void {
  const prefix = '  '.repeat(indent) + colors.dim('•');
  console.log(`${prefix} ${text}`);
}

/**
 * Print JSON output
 */
export function json(data: unknown): void {
  console.log(JSON.stringify(data, null, 2));
}

/**
 * Print a newline
 */
export function newline(): void {
  console.log();
}

/**
 * Format a task status with color
 */
export function formatStatus(status: string): string {
  switch (status) {
    case 'completed':
      return colors.success(`${symbols.success} completed`);
    case 'pending':
      return colors.warning(`${symbols.checkboxOff} pending`);
    case 'in_progress':
      return colors.info('◐ in progress');
    case 'cancelled':
      return colors.dim(`${symbols.error} cancelled`);
    default:
      return status;
  }
}

/**
 * Format a priority with color
 */
export function formatPriority(priority: string): string {
  switch (priority) {
    case 'high':
      return colors.error('▲ high');
    case 'medium':
      return colors.warning('■ medium');
    case 'low':
      return colors.dim('▽ low');
    default:
      return priority;
  }
}

/**
 * Format a date for display
 */
export function formatDate(date: string | Date): string {
  const d = new Date(date);
  const now = new Date();
  const diff = now.getTime() - d.getTime();

  // Future dates (nextRecoveryAt, dueDate, expiresAt, etc.)
  if (diff < 0) {
    const absDiff = -diff;
    const mins = Math.floor(absDiff / (1000 * 60));
    const hours = Math.floor(absDiff / (1000 * 60 * 60));
    const days = Math.floor(absDiff / (1000 * 60 * 60 * 24));
    if (mins < 60) return `in ${mins}m`;
    if (hours < 24) return `in ${hours}h`;
    if (days < 7) return `in ${days} days`;
    return d.toLocaleDateString();
  }

  const days = Math.floor(diff / (1000 * 60 * 60 * 24));

  if (days === 0) {
    return 'today';
  } else if (days === 1) {
    return 'yesterday';
  } else if (days < 7) {
    return `${days} days ago`;
  } else {
    return d.toLocaleDateString();
  }
}

/**
 * Format a date as a granular relative time string (e.g. "5m ago", "2h ago")
 */
export function formatRelativeTime(dateStr: string): string {
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMin = Math.floor(diffMs / 60000);
  const diffHour = Math.floor(diffMin / 60);
  const diffDay = Math.floor(diffHour / 24);

  if (diffMin < 1) return 'just now';
  if (diffMin < 60) return `${diffMin}m ago`;
  if (diffHour < 24) return `${diffHour}h ago`;
  if (diffDay < 7) return `${diffDay}d ago`;
  return date.toLocaleDateString();
}

/**
 * Truncate text to a maximum length
 */
export function truncate(text: string, maxLength: number): string {
  if (text.length <= maxLength) return text;
  return text.slice(0, maxLength - 3) + '...';
}

/**
 * Format a duration in milliseconds to a human-readable string
 */
export function formatDuration(ms: number): string {
  if (ms < 1000) return `${ms}ms`;
  if (ms < 60000) return `${(ms / 1000).toFixed(1)}s`;
  if (ms < 3600000) {
    const mins = Math.floor(ms / 60000);
    const secs = Math.floor((ms % 60000) / 1000);
    return `${mins}m ${secs}s`;
  }
  const hours = Math.floor(ms / 3600000);
  const mins = Math.floor((ms % 3600000) / 60000);
  return `${hours}h ${mins}m`;
}

/**
 * Error recovery hints - maps common error patterns to actionable suggestions
 * Inspired by Codex CLI's approach to user-friendly error messaging
 */
const ERROR_RECOVERY_HINTS: Array<{ pattern: RegExp; hint: string }> = [
  { pattern: /auth|unauthorized|401|token/i, hint: 'Run "paean login" to re-authenticate.' },
  { pattern: /network|ECONNREFUSED|ENOTFOUND|ETIMEDOUT|fetch failed/i, hint: 'Check your internet connection and try again.' },
  { pattern: /timeout|timed out/i, hint: 'The server may be overloaded. Try again in a few moments.' },
  { pattern: /rate.?limit|429|too many requests/i, hint: 'You\'ve been rate-limited. Wait a minute and try again.' },
  { pattern: /permission|EACCES|forbidden|403/i, hint: 'Check file permissions or try running with appropriate access.' },
  { pattern: /not found|404/i, hint: 'The requested resource may have been moved or deleted.' },
  { pattern: /ENOSPC|disk.?full|no space/i, hint: 'Free up disk space and try again.' },
  { pattern: /500|internal server/i, hint: 'Server error. This is likely temporary — try again shortly.' },
];

/**
 * Print an error message with contextual recovery hints
 */
export function errorWithHint(message: string): void {
  error(message);
  const match = ERROR_RECOVERY_HINTS.find(({ pattern }) => pattern.test(message));
  if (match) {
    dim(`  Hint: ${match.hint}`);
  }
}

/**
 * Get a recovery hint for an error message, if any
 */
export function getRecoveryHint(message: string): string | null {
  const match = ERROR_RECOVERY_HINTS.find(({ pattern }) => pattern.test(message));
  return match?.hint ?? null;
}
